import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import FaqAccordion from "@/components/FaqAccordion";
import { OrbitMark } from "@/components/GlobeArt";
import { solutionBySlug, SITE } from "@/config/site";
import NotFound from "@/pages/NotFound";

function Eyebrow({ children }) {
  return (
    <p className="flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.25em] text-cobalt">
      <OrbitMark className="h-4 w-4" />
      {children}
    </p>
  );
}

function FullbleedHero({ s }) {
  return (
    <section className="bg-midnight pt-28 sm:pt-32" data-testid="solution-hero">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>{s.group}</Eyebrow>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-extrabold tracking-tight leading-[1.1] text-white sm:text-5xl">
            {s.tagline}
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/70">{s.what}</p>
          <div className="mt-7 flex flex-wrap items-center gap-3 pb-10">
            <span className="rounded-md border border-white/25 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white/85">
              For: {s.audience}
            </span>
            <span className="rounded-md bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-silver">
              Provider: {s.provider}
            </span>
          </div>
        </Reveal>
      </div>
      <div className="relative">
        <img
          src={s.image}
          alt={s.imageAlt}
          className="h-[300px] w-full object-cover sm:h-[400px] lg:h-[460px]"
          data-testid="solution-hero-image"
        />
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-midnight to-transparent" />
        <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-platinum to-transparent" />
      </div>
    </section>
  );
}

function SplitHero({ s }) {
  return (
    <section className="bg-platinum pt-28 sm:pt-36 pb-14" data-testid="solution-hero">
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <Reveal className="lg:col-span-7">
          <Eyebrow>{s.group}</Eyebrow>
          <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight leading-[1.1] text-ink sm:text-5xl">
            {s.tagline}
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ink/75">{s.what}</p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <span className="rounded-md border border-silver bg-white px-4 py-2 text-xs font-semibold uppercase tracking-wider text-ink/80">
              For: {s.audience}
            </span>
            <span className="rounded-md bg-cobalt/10 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-cobalt">
              Provider: {s.provider}
            </span>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <img
            src={s.image}
            alt={s.imageAlt}
            className="h-[300px] w-full rounded-md border border-silver/60 object-cover shadow-lg sm:h-[400px]"
            data-testid="solution-hero-image"
          />
        </Reveal>
      </div>
    </section>
  );
}

function BandHero({ s }) {
  return (
    <section className="relative overflow-hidden bg-white pt-28 sm:pt-36 pb-16" data-testid="solution-hero">
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-full opacity-[0.12]">
        <svg viewBox="0 0 1440 500" fill="none" preserveAspectRatio="xMidYMid slice" className="h-full w-full">
          <path d="M-100 380 C 320 120, 1120 120, 1540 380" stroke="#0755C9" strokeWidth="1.5" />
          <ellipse cx="720" cy="120" rx="620" ry="150" stroke="#0755C9" strokeWidth="1" strokeDasharray="3 12" />
        </svg>
      </div>
      <div className="relative mx-auto grid max-w-7xl items-end gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
        <Reveal className="lg:col-span-6">
          <Eyebrow>{s.group}</Eyebrow>
          <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight leading-[1.1] text-ink sm:text-5xl">
            {s.tagline}
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-ink/75">{s.what}</p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <span className="rounded-md border border-silver bg-platinum px-4 py-2 text-xs font-semibold uppercase tracking-wider text-ink/80">
              For: {s.audience}
            </span>
            <span className="rounded-md bg-midnight px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white">
              Provider: {s.provider}
            </span>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-6">
          <div className="relative lg:pb-10">
            <img
              src={s.image}
              alt={s.imageAlt}
              className="h-[280px] w-full rounded-md border border-silver/60 object-cover shadow-xl sm:h-[360px]"
              data-testid="solution-hero-image"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function SolutionPage() {
  const { slug } = useParams();
  const s = solutionBySlug(slug);
  if (!s) return <NotFound />;

  return (
    <>
      <Seo
        title={s.name}
        description={`${s.short} ${s.tagline}`}
        path={`/solutions/${s.slug}`}
        image="/djc-og.jpg"
      />

      {s.variant === "fullbleed" && <FullbleedHero s={s} />}
      {s.variant === "split" && <SplitHero s={s} />}
      {s.variant === "band" && <BandHero s={s} />}

      <section className="bg-platinum py-14 sm:py-20" data-testid="solution-explainer">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:gap-14 lg:px-8">
          <Reveal className="lg:col-span-7">
            <h2 className="font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
              What this is, in plain English
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-ink/75">{s.what}</p>
            <p className="mt-4 leading-relaxed text-ink/75">{s.who}</p>
            <h3 className="mt-10 font-display text-xl font-bold text-ink">The business challenge</h3>
            <p className="mt-3 leading-relaxed text-ink/75">{s.challenge}</p>
          </Reveal>
          <Reveal delay={0.08} className="lg:col-span-5">
            <div className="rounded-md border border-silver/60 bg-white p-6 shadow-sm lg:p-7">
              <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-cobalt">
                Areas to explore
              </p>
              <ul className="mt-4 space-y-3">
                {s.capabilities.map((c) => (
                  <li key={c} className="flex gap-2.5 text-sm leading-relaxed text-ink/80">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-cobalt" />
                    {c}
                  </li>
                ))}
              </ul>
              {s.providerNote && (
                <p className="mt-5 border-t border-silver/50 pt-4 text-xs leading-relaxed text-ink/55">
                  {s.providerNote}
                </p>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-14 sm:py-20" data-testid="solution-roles">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
              Who does what
            </h2>
          </Reveal>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-md border-2 border-cobalt/70 bg-platinum p-6 lg:p-8" data-testid="role-djc-card">
                <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-cobalt">
                  DJC Global's role
                </p>
                <p className="mt-3 leading-relaxed text-ink/80">{s.djcRole}</p>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="h-full rounded-md border border-silver/60 bg-platinum p-6 lg:p-8" data-testid="role-provider-card">
                <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-ink/70">
                  {s.provider}'s role
                </p>
                <p className="mt-3 leading-relaxed text-ink/80">{s.providerRole}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-platinum py-14 sm:py-20" data-testid="solution-next">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
              What happens next
            </h2>
          </Reveal>
          <ol className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {s.next.map((n, i) => (
              <Reveal key={n} delay={i * 0.06}>
                <li className="flex h-full flex-col border-t-2 border-cobalt/80 pt-4" data-testid={`solution-next-step-${i}`}>
                  <span className="font-display text-3xl font-extrabold text-silver">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="mt-3 font-display text-[15px] font-semibold leading-snug text-ink">{n}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-white py-14 sm:py-20" data-testid="solution-faq">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
              Questions worth asking
            </h2>
          </Reveal>
          <Reveal delay={0.06} className="mt-8">
            <FaqAccordion items={s.faqs} testPrefix={`${s.slug}-faq`} />
          </Reveal>
        </div>
      </section>

      <section className="bg-midnight py-16 sm:py-20" data-testid="solution-cta">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <Reveal>
            <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
              {s.tagline}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-white/75">
              Start with a conversation with DJC Global, or go straight to {s.provider}. Either way,
              you'll know what happens next.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to={`/contact?solution=${s.slug}`}
                data-testid="solution-inquiry-cta"
                className="group inline-flex w-full items-center justify-center gap-2 rounded-md bg-white px-7 py-3.5 font-display text-sm font-semibold text-midnight transition-colors hover:bg-silver sm:w-auto"
              >
                Inquire With DJC Global
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <a
                href={s.cta.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="solution-outbound-referral-cta"
                className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-white/35 px-7 py-3.5 font-display text-sm font-semibold text-white transition-colors hover:bg-white/10 sm:w-auto"
              >
                {s.cta.label}
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
            <p className="mt-6 text-xs text-white/55">
              Prefer to talk it through first? Call{" "}
              <a href={SITE.phoneHref} data-testid="solution-phone-link" className="font-semibold text-white underline decoration-silver/50 underline-offset-4">
                {SITE.phone}
              </a>
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
